// src/scenes/AuthenticatedMenuScene.js
// v3 - Uses startScene helper, UIController, correct Guest+ handling

import BaseMenuScene from "./BaseMenuScene.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import { signOutUser } from "../services/authService.js";
// Removed showLeaderboardOverlay - now using HallOfFameDesktopTemplate directly
import { populateAnalyticsOverlay } from "../ui/analyticsUI.js";
import { auth, db } from "../firebaseConfig.js";
import UIController from "../ui/uiController.js";
import PPTHeroSceneManager from "./ppt-hero/PPTHeroSceneManager.js";
import {
    hasSpecialAccess, getSpecialAccessHoursRemaining, clearSpecialAccess,
    updateSpecialAccessDisplayName, getSpecialAccessDisplayName
} from "../services/specialAccessService.js";
import guestPlusSessionManager from "../services/GuestPlusSessionManager.js";
import sceneManager from "../utils/SceneManager.js"; // Keep import for safety fallback
import PhilosophyFlowScene from "./PhilosophyFlowScene.js";

class AuthenticatedMenuScene extends BaseMenuScene {
    constructor() {
        super({ key: "AuthenticatedMenuScene" });
        this.centered = false; // Use grid layout
    }

    // src/scenes/AuthenticatedMenuScene.js

    // src/scenes/AuthenticatedMenuScene.js

    async create() {
        super.create();
        this.tiles = [];
        this.setRibbonVisibility(false); // Use the helper from BaseMenuScene

        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();

        // Redirect check
        if (!user && !specialAccess) {
            console.warn("User without access in AuthenticatedMenuScene, redirecting.");
            if (window.fixTransitionState) { window.fixTransitionState(); } // Reset flags if stuck
            const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
            this.startScene(useDOMMenus ? "MainMenuSceneDOM" : "MainMenuScene");
            return;
        }

        // Determine Display Name
        let playerDisplayName = "Player";
        if (user) {
            playerDisplayName = user.email || "User"; // Fallback to email
            try {
                // Fetch profile name asynchronously
                const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
                if (profileDoc.exists && profileDoc.data().displayName) {
                    playerDisplayName = profileDoc.data().displayName;
                }
            } catch(e) { console.error("Failed to fetch profile name:", e); }
        } else if (specialAccess) {
            playerDisplayName = getSpecialAccessDisplayName(); // Get Guest+ name
            try { guestPlusSessionManager.init(); } catch (error) { console.warn("Error initializing Guest+ session manager:", error); }
        }

        // Layout Variables
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const topBarY = 40;
        const tileStartY = 100;
        let footerStartY = height - 120;

        // Create Header Buttons
        this.headerButtons = this.createHeaderButtons(
            topBarY,
            [ { key: 'logoutButton', text: user ? 'Logout' : 'Back',
                 callback: () => {
                     if (user) { signOutUser().catch(e => console.error("Signout error:", e)); }
                     else { clearSpecialAccess(); this.showMessage("Guest+ session ended.", 2000, false); if (window.fixTransitionState) { window.fixTransitionState(); } this.startScene("MainMenuScene"); }
                 },
                 color: MenuUIUtils.COLORS.danger, width: 100 } ],
            [
                { key: 'analyticsButton', text: 'Analytics',
                   callback: async () => {
                       if (user) { 
                         // Feature flag to use new Performance Rating System
                         const useNewPerformanceRating = localStorage.getItem('pptHeroUseNewAnalytics') === 'true';
                         
                         if (useNewPerformanceRating) {
                           console.log('🆕 AuthenticatedMenuScene using new Performance Rating System');
                           try {
                             const { default: PerformanceRatingSystem } = await import('./ppt-hero/modules/PerformanceRatingSystem.js');
                             const performanceSystem = new PerformanceRatingSystem(this);
                             await performanceSystem.showPerformanceOverlay();
                           } catch (error) {
                             console.error('Error loading new Performance Rating System:', error);
                             this.showMessage('Error loading analytics. Please try again.', 3000, true);
                           }
                         } else {
                           console.log('📊 AuthenticatedMenuScene using legacy analytics system');
                           try { await populateAnalyticsOverlay(user.uid); UIController.showOverlay('analytics-overlay'); } catch (err) { console.error("Error preparing analytics:", err); this.showMessage("Could not load analytics data.", 3000, true); } 
                         }
                       }
                       else if (specialAccess) { this.showMessage("Analytics not available in Guest+ mode.", 3000, true); }
                   }, color: MenuUIUtils.COLORS.accent2, width: 120 },
                { key: 'privacyButton', text: 'Privacy', callback: () => { UIController.showOverlay('privacy-overlay'); }, color: MenuUIUtils.COLORS.accent3, width: 120 },
                { key: 'usernameLabel', text: playerDisplayName, isLabel: true, color: MenuUIUtils.COLORS.primary }
            ]
        );

        // Check admin status
        this.isAdmin = false;
        if (user) {
            try {
                const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
                if (profileDoc.exists && profileDoc.data().role === 'admin') { this.isAdmin = true; }
            } catch (error) { console.error("Error checking admin status:", error); }
        }

        // --- Define Tile Data with 'teamComingSoon' ---
        const tileData = [
            {
                title: "PPT HERO",
                color: MenuUIUtils.COLORS.accent2, // Blue
                solo: () => this.startPPTHero(),
                teamComingSoon: true, // Use this flag for greyed-out but clickable
                hasToggle: true,
                toggleLabel: PPTHeroSceneManager.getModeDisplayNameSync(),
                onToggle: () => this.togglePPTHeroMode(),
                getUpdatedLabel: () => PPTHeroSceneManager.getModeDisplayNameSync()
            },
            {
                title: "Slide Wars",
                color: MenuUIUtils.COLORS.accent1, // Orange
                soloComingSoon: true, // Grey out solo mode - coming soon
                soloComingSoonMessage: "Slide Wars is still in development - coming soon!",
                teamComingSoon: true, // Grey out team mode - coming soon
                teamComingSoonMessage: "Slide Wars is still in development - coming soon!"
            },
            {
                title: "Shortcut Mastery",
                color: MenuUIUtils.COLORS.warning, // Yellow
                soloComingSoon: true, // Grey out solo mode - coming soon
                soloComingSoonMessage: "Shortcut Mastery is still in development - coming soon!",
                teamComingSoon: true, // Grey out team mode - coming soon
                teamComingSoonMessage: "Shortcut Mastery is still in development - coming soon!"
            },
           {
               title: "Philosophy Flow",
               color: MenuUIUtils.COLORS.accent3, // Purple
               // --- FIXED: Put the actual solo logic back ---
               solo: (function() {
                   console.log("[DIAGNOSTIC] Philosophy Flow button clicked");
                   try {
                       const user = auth.currentUser; // Get current user state inside callback
                       console.log("[DIAGNOSTIC] Auth check - User:", user ? user.uid : 'null', "Verified:", user?.emailVerified, "isAdmin:", this.isAdmin, "Guest+:", hasSpecialAccess());
                       // Allow verified users, admins, OR Guest+ users
                       if ((user && user.emailVerified) || this.isAdmin || hasSpecialAccess()) {
                           console.log("[DIAGNOSTIC] Access check passed (Verified, Admin, or Guest+)");
                           this.startScene("PhilosophyFlowScene"); // Use the scene starting helper
                           console.log("[DIAGNOSTIC] startScene called successfully");
                       } else {
                           console.log("[DIAGNOSTIC] Access check failed, showing message");
                           this.showMessage("Sign in & verify email for Philosophy Flow.", 3000, false);
                       }
                   } catch (error) {
                       console.error("[DIAGNOSTIC] Error in Philosophy Flow button handler:", error);
                       this.showMessage("Error launching game.", 2000, true); // Show generic error
                   }
               }).bind(this), // Keep .bind(this) to maintain scene context
               // --- END FIX ---
               teamComingSoon: true, // Use this flag
               teamComingSoonMessage: "Philosophy Flow is a solo game." // Custom message
            },
            {
               title: "Classify Tech",
               color: MenuUIUtils.COLORS.accent4, // Lime Green/Yellow
               soloComingSoon: true, // Grey out solo mode - coming soon
               soloComingSoonMessage: "Classify Tech is still in development - coming soon!",
               teamComingSoon: true, // Grey out team mode - coming soon
               teamComingSoonMessage: "Classify Tech is still in development - coming soon!"
            },
            {
                title: user ? "User Profile" : "Guest Profile",
                color: MenuUIUtils.COLORS.textMedium,
                utility: () => {
                      if (user) { 
                        // Feature flag to use DOM-based Profile Scene
                        const useDOMProfile = localStorage.getItem('pptHeroUseDOMProfile') === 'true';
                        if (useDOMProfile) {
                          this.startScene("ProfileSceneDOM");
                        } else {
                          this.startScene("ProfileLoadingScene"); // Uses Rex UI ProfileScene
                        }
                      }
                      else { this.showMessage("Profile features limited in Guest+ mode", 2000, false); }
                }
            },
            {
                title: "Hall of Fame",
                color: MenuUIUtils.COLORS.warning,
                utility: async () => {
                    try { 
                        console.log('🏆 Opening new desktop Hall of Fame template from authenticated menu...');
                        this.startScene('HallOfFameDesktopTemplate'); 
                    }
                    catch (e) { console.error("Error showing leaderboard:", e); this.showMessage("Error loading leaderboard.", 2000, true); }
                }
            }
        ];
        // --- End Tile Data ---

        // Create the grid
        this.createGameTileGrid(tileStartY, tileData, {
            maxTileWidth: 280, tileHeight: 130, horizontalGap: 20, verticalGap: 20, bottomPadding: 150
        });

        // --- Specific UI for Guest+ Users (Keep as is) ---
        if (specialAccess && !user) {
            const hoursRemaining = getSpecialAccessHoursRemaining();
            this.uiElements.expiryNotice = this.add.text( width / 2, footerStartY, `Special access expires in ${hoursRemaining} hours`, { fontFamily: 'monospace', fontSize: '14px', color: hoursRemaining <= 1 ? MenuUIUtils.COLORS.danger : MenuUIUtils.COLORS.primary, fontWeight: 'bold' } ).setOrigin(0.5).setDepth(100);
            footerStartY += 40;
            this.uiElements.signInButton = this.createStyledButton( width / 2, footerStartY, "Sign In to Save Progress", () => { if (window.UIController) { window.UIController.showOverlay('auth-overlay'); } }, MenuUIUtils.COLORS.secondary, 250, 40 );
            footerStartY += 40;
            this.uiElements.endSessionButton = this.createStyledButton( width / 2, footerStartY, "End Guest+ Session", () => { const confirmed = confirm("Are you sure you want to end your Guest+ session?"); if (confirmed) { clearSpecialAccess(); this.showMessage("Guest+ session ended.", 2000, false); if (window.fixTransitionState) { window.fixTransitionState(); } this.startScene("MainMenuScene"); } }, MenuUIUtils.COLORS.danger, 250, 40 );
        }
        // --- End Guest+ UI ---

        // Ensure initial positioning
        this.handleResize();
        
        // Override BaseMenuScene - AuthenticatedMenuScene async UI build complete
        this.isUIReady = true;
        console.debug(`[${this.scene.key}] AuthenticatedMenuScene async UI build complete, ready for fade-in`);
    } // End create method

    handleResize(gameSize) {
        super.handleResize(gameSize);
        if (!this.cameras?.main) return;
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const topBarY = 40;
        let footerStartY = height - 120;

        if (this.headerButtons) {
            if (this.headerButtons.logoutButton) { this.headerButtons.logoutButton.setPosition(this.padding + (this.headerButtons.logoutButton.width / 2), topBarY); }
            let rightX = width - this.padding;
            const usernameLabelElement = this.uiElements.usernameLabel || this.headerButtons.usernameLabel;
            if (usernameLabelElement) {
                usernameLabelElement.setPosition(rightX, topBarY);
                const nameWidth = usernameLabelElement.width || (usernameLabelElement.text.length * 10);
                rightX -= (nameWidth + 15);
            }
            if (this.headerButtons.privacyButton) { this.headerButtons.privacyButton.setPosition(rightX - (this.headerButtons.privacyButton.width / 2), topBarY); rightX -= (this.headerButtons.privacyButton.width + 10); }
            if (this.headerButtons.analyticsButton) { this.headerButtons.analyticsButton.setPosition(rightX - (this.headerButtons.analyticsButton.width / 2), topBarY); }
        }

        const specialAccess = hasSpecialAccess();
        const user = auth.currentUser;
        if (specialAccess && !user) {
            if (this.uiElements.expiryNotice) { this.uiElements.expiryNotice.setPosition(width / 2, footerStartY); footerStartY += 40; }
            if (this.uiElements.signInButton) { this.uiElements.signInButton.setPosition(width / 2, footerStartY); footerStartY += 40; }
            if (this.uiElements.endSessionButton) { this.uiElements.endSessionButton.setPosition(width / 2, footerStartY); }
        }
    }

    shutdown() {
        console.log(`[${this.scene.key}] Shutting down...`);
        super.shutdown();
        console.log(`[${this.scene.key}] Shutdown complete.`);
    }

    // PPT Hero mode management
    async startPPTHero() {
        const currentMode = PPTHeroSceneManager.getCurrentMode();
        await PPTHeroSceneManager.startPPTHero(this, currentMode);
    }

    async togglePPTHeroMode() {
        const newMode = PPTHeroSceneManager.toggleMode();
        console.log(`PPT Hero mode toggled to: ${newMode}`);
        
        // Update the UI to reflect the new mode
        this.updatePPTHeroToggleLabel();
        
        // Show brief message about mode change
        const modeDisplayName = await PPTHeroSceneManager.getModeDisplayName(newMode);
        this.showMessage(`PPT Hero mode: ${modeDisplayName}`, 1500);
    }

    updatePPTHeroToggleLabel() {
        // Update the toggle label on the PPT Hero card
        // This would be called by MenuUIUtils when the toggle is clicked
        const newLabel = PPTHeroSceneManager.getModeDisplayNameSync();
        
        // Find and update the PPT Hero tile toggle label
        if (this.uiElements && this.uiElements.gameCards) {
            const pptHeroCard = this.uiElements.gameCards.find(card => card.title === "PPT HERO");
            if (pptHeroCard && pptHeroCard.toggleLabel) {
                pptHeroCard.toggleLabel.setText(newLabel);
            }
        }
    }

    showMessage(text, duration = 1500) {
        // Simple message display (reuse from other scenes if available)
        if (this.messageText) {
            this.messageText.destroy();
        }
        
        this.messageText = this.add.text(
            this.cameras.main.width / 2,
            this.cameras.main.height - 100,
            text,
            {
                fontSize: "16px",
                color: "#00ff00",
                backgroundColor: "#000",
                padding: { x: 10, y: 5 }
            }
        );
        this.messageText.setOrigin(0.5);
        this.messageText.setDepth(1000);

        // Auto-remove after duration
        this.time.delayedCall(duration, () => {
            if (this.messageText) {
                this.messageText.destroy();
                this.messageText = null;
            }
        });
    }
}

export default AuthenticatedMenuScene;